import React, { useMemo } from "react";
import {
  DOWNLOAD_FORMATS,
  useAssetDownload,
} from "domains/assets/hooks/useAssetDownload";
import { useHotkeys } from "domains/commons/contexts/HotkeysProvider";
import { appShortcuts } from "domains/shortcuts/components/appShortcuts";
import Button from "domains/ui/components/Button";
import Icon from "domains/ui/components/Icon";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import {
  GetAssetsByAssetIdApiResponse,
  GetModelsByModelIdApiResponse,
  GetModelsInferencesByModelIdAndInferenceIdApiResponse,
} from "infra/api/generated/api";

import { Menu, MenuButton, MenuList } from "@chakra-ui/react";

export interface ButtonDownloadProps {
  model: GetModelsByModelIdApiResponse["model"] | undefined;
  inference:
    | GetModelsInferencesByModelIdAndInferenceIdApiResponse["inference"]
    | undefined;
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined;
}

export default function ButtonDownload({
  model,
  inference,
  asset,
}: ButtonDownloadProps) {
  const { handleDownload, isDownloading } = useAssetDownload({
    asset,
    model,
    inference,
    trackingEvent: AnalyticsEvents.AssetZoom.Downloaded,
  });

  const availableFormats: DOWNLOAD_FORMATS[] = useMemo(() => {
    if (!asset) {
      return [];
    }
    if (asset.metadata.kind === "3d" || asset.metadata.kind === "video") {
      return ["raw"];
    }
    if (
      asset.mimeType === "image/svg+xml" ||
      asset.metadata.type === "vectorization"
    ) {
      return ["svg"];
    }
    if (asset.metadata.type === "background-removal") {
      return ["png"];
    }
    return ["jpeg", "png", "webp"];
  }, [asset]);

  useHotkeys(
    appShortcuts.assetPage.shortcuts.download.shortcut,
    () => {
      if (asset?.metadata.type === "texture") {
        void handleDownload("texture");
      } else {
        const format = availableFormats.includes("png")
          ? "png"
          : availableFormats[0];
        void handleDownload(format);
      }
    },
    [availableFormats, handleDownload, asset]
  );

  return asset?.metadata.type === "texture" ? (
    <Button
      variant="secondary"
      size="sm"
      isLoading={isDownloading}
      onClick={() => handleDownload("texture")}
      leftIcon={<Icon id="Ui/Download" h="16px" />}
    >
      Download Maps
    </Button>
  ) : (
    <Menu>
      <MenuButton
        as={Button}
        p={2}
        isDisabled={!asset}
        isLoading={isDownloading}
        onClick={
          availableFormats.length === 1
            ? (e) => {
                e.preventDefault();
                void handleDownload(availableFormats[0]);
              }
            : undefined
        }
        size="sm"
        variant="secondary"
      >
        <Icon id="Ui/Download" h="18px" />
      </MenuButton>

      <MenuList>
        {availableFormats.includes("png") && (
          <MenuItem
            onClick={() => handleDownload("png")}
            text="PNG"
            tooltipProps={{
              placement: "bottom",
            }}
          />
        )}

        {availableFormats.includes("jpeg") && (
          <MenuItem onClick={() => handleDownload("jpeg")} text="JPEG" />
        )}

        {availableFormats.includes("webp") && (
          <MenuItem
            onClick={() => handleDownload("webp")}
            text="WebP"
            tooltipProps={{
              placement: "bottom",
            }}
          />
        )}
      </MenuList>
    </Menu>
  );
}
